<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  useDate,
  AtomText,
  AtomButton,
  AtomRadio,
  SatFormControl,
  WoLocation,
} from "@chatfood/design-system";
import { ref, onMounted, computed } from "vue";
import { SchedulePicker } from "../schedule-picker";
import { BasketEventName } from "../basket-event-name";
import { BasketPax } from "../basket-pax";
import type { IDeliveryOrderingDetailsEmits } from "./emits";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useItemSchedulesStore } from "~/stores/itemSchedules";
import { useNavigation } from "~/composables/use-navigation";
import { useLocale } from "~/composables/use-locale";
import { useMinimumLeadTime } from "~/composables/use-minimum-lead-time";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import type { IDomainOrderScheduledTime } from "~/domain/order-scheduled-time";
import type { IDomainOrderingModeSettings } from "~/domain/ordering-mode-settings";
import { useBugReporter } from "#imports";

const emits = defineEmits<IDeliveryOrderingDetailsEmits>();

const css = bemBuilder("delivery-ordering-details");

const { t } = useLocale();
const { formatTime, getReadableDate } = useDate();
const { resolvePath } = useNavigation();
const { report } = useBugReporter();

const business = useBusinessStore();
const outlet = useOutletStore();
const basket = useBasketStore();
const customer = useCustomerStore();
const itemSchedulesStore = useItemSchedulesStore();

type IOrderType = "asap" | "scheduled";

const DELIVERY = "delivery";

const hasScheduledDeliveryBasket =
  basket.orderingMode === DELIVERY && !!basket.scheduledTime;

const canOrderAsapNow =
  outlet.orderingModes.delivery.acceptsAsapOrders &&
  outlet.orderingModes.delivery.outletState === "open";

const orderType = ref<IOrderType>(
  !hasScheduledDeliveryBasket && canOrderAsapNow ? "asap" : "scheduled",
);

const scheduledTime = ref<IDomainOrderScheduledTime | undefined>(
  basket.orderingMode === DELIVERY ? basket.scheduledTime : undefined,
);

const isSettingMode = ref(false);

const scheduleErrorMessage = ref<string>();

const freshOrderingModeSettings = ref<IDomainOrderingModeSettings>();

const orderingModeSettings = computed(() => {
  return freshOrderingModeSettings.value ?? outlet.orderingModes.delivery;
});

const displayAsapRadioOption = computed(() => {
  return (
    orderingModeSettings.value.acceptsAsapOrders &&
    orderingModeSettings.value.outletState === "open"
  );
});

const customerAddress = computed(() => {
  return customer.addresses.find((address) => address.id === basket.addressId);
});

const deliveryLocation = computed(
  () => customerAddress.value?.location ?? basket.currentLocation?.address,
);

const onConfirm = async () => {
  if (!basket.isLoaded) {
    console.error(
      "[DeliveryOrderingDetails] Cannot confirm delivery without a basket.",
    );
    return;
  }

  isSettingMode.value = true;

  const chosenTime = scheduledTime.value;

  try {
    if (business.isCatering) {
      await basket.setMeta({
        eventName: eventName.value ?? null,
        pax: typeof pax.value === "number" ? pax.value : null,
      });
    }

    const { validateScheduleTime } = useMinimumLeadTime();

    const itemSchedules = itemSchedulesStore.schedulesByOutlet[basket.outletId];

    const rescheduleValidation = validateScheduleTime(
      basket.items,
      chosenTime,
      (itemName) =>
        t("components.delivery_ordering_details.reschedule_item_error", {
          itemName,
        }),
      itemSchedules,
    );

    if (!rescheduleValidation.isValid) {
      scheduleErrorMessage.value = rescheduleValidation.errors;
      return;
    }

    await basket.setScheduledTime(chosenTime);

    emits("update");
  } catch (e) {
    report(e);
    scheduleErrorMessage.value = t(
      "components.delivery_ordering_details.error_confirm_failed",
    );
  } finally {
    isSettingMode.value = false;
  }
};

const onSetSchedule = (value?: IDomainOrderScheduledTime) => {
  scheduleErrorMessage.value = undefined;
  scheduledTime.value = value;
};

const isButtonDisabled = computed<boolean>(() => {
  if (
    orderingModeSettings.value.acceptsPreOrders &&
    orderType.value === "scheduled" &&
    !scheduledTime.value
  ) {
    return true;
  }

  return !customerAddress.value && !basket.currentLocation;
});

const initStates = async () => {
  if (!basket.isLoaded) return;

  scheduledTime.value = basket.scheduledTime;

  const deliverySettings = outlet.orderingModes.delivery;

  if (
    !deliverySettings.acceptsAsapOrders &&
    deliverySettings.acceptsPreOrders
  ) {
    orderType.value = "scheduled";
  }
};

onMounted(initStates);

const eventName = ref(basket.eventName);
const pax = ref(basket.pax);

const handleChangeOrderType = (type: IOrderType) => {
  scheduleErrorMessage.value = undefined;
  orderType.value = type;
};

const deliveryEta = computed(() => {
  if (scheduledTime.value) {
    return t("components.delivery_ordering_details.scheduled_delivery", {
      date: getReadableDate(scheduledTime.value.day, "ll"),
      time: formatTime(scheduledTime.value.time),
    });
  }

  const eta = outlet.orderingMode.estimatedCompletionMinutes;

  return t("components.delivery_ordering_details.eta_delivery", {
    from: eta,
    to: eta + ETA_PREPARATION_MARGIN,
  });
});
</script>

<template>
  <div :class="css('')">
    <div :class="css('mode-config')">
      <AtomText type="subtitle">
        {{ t("components.delivery_ordering_details.title") }}
      </AtomText>
      <WoLocation
        v-if="deliveryLocation"
        is-active
        state="close"
        :class="css('cursor-default')"
        :name="
          customerAddress?.nickname ??
          t('components.delivery_ordering_details.current_location')
        "
        :address="deliveryLocation"
        :state-label="deliveryEta"
        :state-description="''"
      />
      <NuxtLink :to="resolvePath('order', { orderingMode: DELIVERY })">
        <AtomText type="link" color="color-primary">
          {{
            deliveryLocation
              ? t(
                  "components.delivery_ordering_details.choose_another_location",
                )
              : t("components.delivery_ordering_details.select_location")
          }}
        </AtomText>
      </NuxtLink>
    </div>
    <div v-if="business.isCatering" :class="css('meta-fields')">
      <AtomText type="subtitle">
        {{ t("components.delivery_ordering_details.details") }}
      </AtomText>
      <BasketEventName @update="(value) => (eventName = value)" />
      <BasketPax @update="(value) => (pax = value)" />
    </div>
    <div
      v-if="displayAsapRadioOption || orderingModeSettings.acceptsPreOrders"
      :class="css('scheduler')"
    >
      <SatFormControl :error-message="scheduleErrorMessage">
        <AtomText type="subtitle">
          {{ t("components.delivery_ordering_details.schedule_title") }}
        </AtomText>
        <AtomRadio
          v-if="displayAsapRadioOption"
          name="deliveryScheduleOrder"
          :checked="orderType === 'asap'"
          value="asap"
          data-test="order-type-asap"
          @change="handleChangeOrderType('asap')"
        >
          {{
            t("components.delivery_ordering_details.asap_radio", {
              from: orderingModeSettings.estimatedCompletionMinutes,
              to:
                orderingModeSettings.estimatedCompletionMinutes +
                ETA_PREPARATION_MARGIN,
            })
          }}
        </AtomRadio>
        <AtomRadio
          v-if="orderingModeSettings.acceptsPreOrders"
          name="deliveryScheduleOrder"
          :checked="orderType === 'scheduled'"
          value="scheduled"
          data-test="order-type-scheduled"
          @change="handleChangeOrderType('scheduled')"
        >
          {{ t("components.delivery_ordering_details.schedule_radio") }}
        </AtomRadio>
        <SchedulePicker
          v-if="
            orderingModeSettings.acceptsPreOrders && orderType === 'scheduled'
          "
          :is-root="true"
          :show-label="false"
          :ordering-mode="DELIVERY"
          :outlet-id="basket.outletId"
          :sales-channel="basket.salesChannel"
          picker-align="row"
          :value="scheduledTime"
          @select="onSetSchedule"
          @vue:unmounted="() => onSetSchedule()"
        />
      </SatFormControl>
    </div>
    <div :class="css('footer')">
      <AtomButton
        wide
        size="large"
        :is-loading="isSettingMode"
        :is-disabled="isButtonDisabled"
        data-test="cta"
        @click="onConfirm"
      >
        {{ t("components.delivery_ordering_details.main_cta") }}
      </AtomButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.delivery-ordering-details {
  min-width: 320px;

  &__mode-config {
    display: flex;
    overflow: auto;
    max-height: 350px;
    gap: var(--spacing-lg);
    flex-direction: column;
    padding: var(--spacing-lg) var(--spacing-md);
    border-bottom: 1px solid var(--color-base-95);

    @media (width < $breakpoint-lg) {
      max-height: 250px;
    }
  }

  &__meta-fields {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: column;
    border-bottom: 1px solid var(--color-base-95);
    padding: var(--spacing-md) var(--spacing-md) var(--spacing-lg);
  }

  &__scheduler {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    padding: var(--spacing-lg) var(--spacing-md);
    border-bottom: 1px solid var(--color-base-95);
  }

  &__cursor-default {
    cursor: default;
  }

  &__footer {
    padding: var(--spacing-lg) var(--spacing-md);
  }
}
</style>
